import {
  useCallback,
  type ChangeEvent,
  type FunctionComponent,
  type KeyboardEventHandler,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { Radio } from '@atlaskit/radio';
import { isSubmitEvent } from '@trello/keybindings';
import { Button } from '@trello/nachos/button';
import { token } from '@trello/theme';

import { AutosizeTextarea } from 'app/src/components/AutosizeTextarea';
import type { SmartScheduleOnboardingAiRuleType } from '../useUpsertSmartScheduleOnboardingAiRule';
import { SmartScheduleRuleTypeInfoButton } from './SmartScheduleRuleTypeInfoButton';

import * as styles from './SmartScheduleRuleSection.module.less';

interface SmartScheduleRuleSectionProps {
  rule: string;
  onRuleChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  ruleType: SmartScheduleOnboardingAiRuleType;
  onRuleTypeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onAddRule: () => void;
  isRuleSaved: boolean;
  onRuleTypeInfoClick: (ruleType: SmartScheduleOnboardingAiRuleType) => void;
}

export const SmartScheduleRuleSection: FunctionComponent<
  SmartScheduleRuleSectionProps
> = ({
  rule,
  onRuleChange,
  ruleType,
  onRuleTypeChange,
  onAddRule,
  isRuleSaved,
  onRuleTypeInfoClick,
}) => {
  const intl = useIntl();

  // Can't add an empty rule, and a saved rule has nothing new to add until it's
  // edited again.
  const isAddRuleDisabled = rule.trim().length === 0 || isRuleSaved;

  const placeholder =
    ruleType === 'PLANNER_SCHEDULING'
      ? intl.formatMessage({
          id: 'templates.smart_schedule.onboarding.rule-placeholder-scheduling',
          defaultMessage: 'Schedule personal errands after 5pm',
          description:
            'Placeholder example for the scheduling preference rule input',
        })
      : intl.formatMessage({
          id: 'templates.smart_schedule.onboarding.rule-placeholder',
          defaultMessage:
            "For example: Always schedule cards with label 'urgent'",
          description: 'Placeholder example for the rule input',
        });

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
    (event) => {
      if (isSubmitEvent(event) && !isAddRuleDisabled) {
        event.preventDefault();
        onAddRule();
      }
    },
    [onAddRule, isAddRuleDisabled],
  );

  return (
    <div className={styles.container}>
      <AutosizeTextarea
        className={styles.textarea}
        minHeight={112}
        maxLength={512}
        name="smartScheduleRule"
        onChange={onRuleChange}
        onKeyDown={onKeyDown}
        value={rule}
        placeholder={placeholder}
      />
      <div className={styles.footer}>
        <div
          className={styles.radioGroup}
          role="radiogroup"
          aria-label={intl.formatMessage({
            id: 'templates.smart_schedule.onboarding.rule-type-aria-label',
            defaultMessage: 'Rule type',
            description: 'Accessible label for the rule type radio group',
          })}
        >
          <div className={styles.radioGroupItem}>
            <Radio
              value="PLANNER_SELECTION"
              label={
                <FormattedMessage
                  id="templates.smart_schedule.onboarding.rule-type-card"
                  defaultMessage="Card"
                  description="Radio option for a card selection rule"
                />
              }
              isChecked={ruleType === 'PLANNER_SELECTION'}
              onChange={onRuleTypeChange}
            />
            <SmartScheduleRuleTypeInfoButton
              ruleType="PLANNER_SELECTION"
              onInfoClick={onRuleTypeInfoClick}
            />
          </div>
          <div className={styles.radioGroupItem}>
            <Radio
              value="PLANNER_SCHEDULING"
              label={
                <FormattedMessage
                  id="templates.smart_schedule.onboarding.rule-type-schedule-preference"
                  defaultMessage="Schedule preference"
                  description="Radio option for a scheduling preference rule"
                />
              }
              isChecked={ruleType === 'PLANNER_SCHEDULING'}
              onChange={onRuleTypeChange}
            />
            <SmartScheduleRuleTypeInfoButton
              ruleType="PLANNER_SCHEDULING"
              onInfoClick={onRuleTypeInfoClick}
            />
          </div>
        </div>
        <div className={styles.addRuleGroup}>
          <span
            className={`${styles.savedIndicator} ${
              isRuleSaved ? styles.savedIndicatorVisible : ''
            }`}
            aria-hidden={!isRuleSaved}
          >
            <StatusSuccessIcon
              label="Rule saved"
              color={token('color.icon.success', '#22A06B')}
            />
          </span>
          <Button
            className={styles.button}
            appearance="primary"
            isDisabled={isAddRuleDisabled}
            onClick={onAddRule}
          >
            <FormattedMessage
              id="templates.smart_schedule.onboarding.save"
              defaultMessage="Save"
              description="Button to save the entered rule"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
